import { RefAttributes, useCallback, useRef } from "react";
import {
    NativeSyntheticEvent,
    Platform,
    TextInput,
    TextInputKeyPressEventData,
    TextInputProps,
} from "react-native";

export type ChainTextInputProps = Pick<
    TextInputProps,
    "blurOnSubmit" | "returnKeyType" | "onSubmitEditing" | "submitBehavior" | "onKeyPress"
> &
    RefAttributes<TextInput>;

export type ChainInputOptions = {
    /** Pass the input's own `disabled`/`editable={false}` state so the chain skips it. */
    disabled?: boolean;
};

type ChainEntry = { fieldName: string; ref?: TextInput; disabled?: boolean };

export const useChainInputFocus = () => {
    const refs = useRef<ChainEntry[]>([]);

    /** Instance of an input already registered by `chainInputFocus`, to focus it imperatively. */
    const getInputRef = useCallback(
        (fieldName: string) => refs.current.find((e) => e.fieldName === fieldName)?.ref,
        [],
    );

    const chainInputFocus = useCallback(
        (fieldName: string, { disabled = false }: ChainInputOptions = {}): ChainTextInputProps => {
            let entry = refs.current.find((e) => e.fieldName === fieldName);
            if (!entry) {
                entry = { fieldName };
                refs.current.push(entry);
            }
            entry.disabled = disabled;
            const index = refs.current.indexOf(entry);
            const currentInput = entry.ref;

            // A disabled input must never be targeted: on Fabric, React Native does not refuse
            // focus() on a non-editable input (its `editable` guard reads a prop the host instance
            // does not carry), so it records the input as focused while the native focus fails.
            // From there, neither blur() nor Keyboard.dismiss() can close the keyboard anymore.
            const findNextInput = () =>
                refs.current.slice(index + 1).find((e) => e.ref && !e.disabled)?.ref;

            const goToNext = () => {
                findNextInput()?.focus?.();
                currentInput?.blur?.();
            };

            const submitProps: ChainTextInputProps = findNextInput()
                ? {
                      submitBehavior: "submit",
                      returnKeyType: "next",
                      ...(Platform.OS === "web"
                          ? {
                                onKeyPress: (
                                    e: NativeSyntheticEvent<TextInputKeyPressEventData>,
                                ) => {
                                    if (e.nativeEvent.key === "Tab") {
                                        e.preventDefault();
                                        goToNext();
                                    }
                                },
                            }
                          : {
                                onSubmitEditing: goToNext,
                            }),
                  }
                : {
                      submitBehavior: "blurAndSubmit",
                      returnKeyType: "done",
                  };

            return {
                ref: (ref: TextInput | null) => {
                    entry.ref = ref ?? undefined;
                },
                ...submitProps,
            };
        },
        [],
    );

    return { chainInputFocus, getInputRef };
};
